/**
 * The shared-directory properties stamped on the Part 1 `patient_*` events
 * (COLAB-422, child of COLAB-419).
 *
 * One definition of each property, so no emit site computes them by hand.
 * `workspace_id` is deliberately absent: the `workspace_context` entity already
 * carries the active workspace on every Snowplow event.
 *
 * Every field is optional and the published Iglu schemas type each as nullable,
 * so an input the frontend cannot resolve at emit time produces an OMITTED
 * property. The builder never guesses: an unknown manager is not a peer's
 * patient, and an unresolved directory gate is not "directory off".
 *
 * Pure and inert. The values it reads (the gate, the actor, the profile) are
 * resolved app-side and passed in, so this module imports nothing.
 */

import type { PatientSearchScope } from './patient-search-analytics.types';

/**
 * The directory state every one of the 11 Part 1 events carries. It is all the
 * two events with no patient (`patient_list_viewed`, `patient_searched`) can
 * carry, so their payload types extend this and not
 * {@link PatientSharedEventProps}.
 */
export type PatientDirectoryEventProps = {
  /** `true` when the shared directory is enabled, `false` when the gate resolved to off. */
  directory_shared?: boolean;
};

/**
 * The properties for the nine events about one patient. Clinician counts are
 * not here: the count comes from the warehouse by joining events to sessions,
 * not from the client.
 */
export type PatientSharedEventProps = PatientDirectoryEventProps & {
  /** `true` when the profile's manager is the acting clinician. */
  patient_is_own?: boolean;
};

export type PatientSharedEventInputs = {
  /**
   * The shared patient directory compound gate, tri-state:
   * `true` on, `false` resolved off, `undefined` not resolved yet.
   */
  directoryEnabled: boolean | undefined;
  /** The acting clinician's `user_ref_id`. */
  actorUserRefId: string | null | undefined;
  /** The profile's `managed_by_user_ref_id`. Absent or `null` when the manager is unnamed. */
  managedByUserRefId: string | null | undefined;
};

/**
 * Builds the directory state alone, for an event with no patient. The key is
 * omitted while the gate is unresolved: unresolved is not "directory off".
 */
export function buildPatientDirectoryEventProps(
  directoryEnabled: boolean | undefined
): PatientDirectoryEventProps {
  return directoryEnabled === undefined ? {} : { directory_shared: directoryEnabled };
}

/**
 * Builds the shared properties for an event about one patient from resolved
 * inputs. Only the keys the inputs can support are present on the result.
 */
export function buildPatientSharedEventProps(
  inputs: PatientSharedEventInputs
): PatientSharedEventProps {
  const props: PatientSharedEventProps = buildPatientDirectoryEventProps(
    inputs.directoryEnabled
  );

  // A manager or an actor that is absent, null or empty cannot be compared:
  // `undefined === undefined` would hand an unidentified actor every unnamed
  // profile as "own".
  if (inputs.actorUserRefId && inputs.managedByUserRefId) {
    props.patient_is_own = inputs.managedByUserRefId === inputs.actorUserRefId;
  }

  return props;
}

/**
 * Whether a link or unlink crosses clinicians: the session's owner differs from
 * the acting clinician.
 *
 * Both sides MUST be in the same id space. The frontend holds a session's owner
 * as `kinde_gp_id`, so the caller passes the actor's `kinde_user_id` here, not
 * the `user_ref_id` the profile manager uses. Only the boolean leaves the
 * client, so the id space is internal to the comparison.
 *
 * - `true` when at least one session has a known, different owner.
 * - `false` when every owner is known and equals the actor.
 * - `null` when the actor is unknown, there are no sessions, or an owner is
 *   unknown and none is known to differ.
 *
 * It reads only ownership: a count of clinicians after a link says nothing
 * about who owned the session before it.
 */
export function resolveIsCrossClinicianLink(
  actorId: string | null | undefined,
  sessionOwnerIds: ReadonlyArray<string | null | undefined>
): boolean | null {
  if (!actorId || sessionOwnerIds.length === 0) return null;
  if (sessionOwnerIds.some((ownerId) => !!ownerId && ownerId !== actorId)) return true;
  return sessionOwnerIds.every((ownerId) => ownerId === actorId) ? false : null;
}

/**
 * The shared patient directory compound gate, tri-state, for emit-time reads.
 *
 * - `false`: the flag is off, or the flag is on and the setting resolved off.
 * - `undefined`: the flag is on and the setting is pending or unavailable.
 * - `true`: the flag is on and the setting is on.
 *
 * The one definition of the rule. The profile and document delete permission
 * contexts and the analytics sync all import it, so the gate cannot drift
 * between them. It is pure, with no React, so the analytics sync can resolve it
 * without a UI hook, and `buildPatientSharedEventProps` keeps unresolved apart
 * from off.
 */
export function resolveDirectoryGate(
  flagEnabled: boolean,
  settingEnabled: boolean | undefined,
  isSettingPending: boolean
): boolean | undefined {
  if (!flagEnabled) return false;
  if (isSettingPending || settingEnabled === undefined) return undefined;
  return settingEnabled;
}

/**
 * The `scope` of a patient search, derived from the directory gate: with the
 * directory enabled the search covers the workspace, otherwise the caller's own
 * profiles. The frontend has no scope control, so this restates a rule the
 * directory defines. An unresolved gate gives `undefined`, never a guess.
 */
export function resolvePatientSearchScope(
  directoryEnabled: boolean | undefined
): PatientSearchScope | undefined {
  if (directoryEnabled === undefined) return undefined;
  return directoryEnabled ? 'workspace' : 'own';
}
